import { DateTime, Interval, ToHumanDurationOptions } from "luxon";

export function isValidDate(d: Date): boolean {
    return DateTime.fromJSDate(d).isValid;
}

export function startOfUSWeek(dt: DateTime): DateTime {
    if (dt.weekday == 7) {
        // We're already on a Sunday, just need to go to the start of the day
        return dt.startOf('day');
    }
    // Luxon weeks start on Monday, so subtract 1 day
    return dt.startOf('week').minus({day: 1})
}

// Converts a DateTime into the browser's local time.
// Useful for things like ant design pickers that don't support timezones.
export function applyTimezone(dt: DateTime): Date {
    return dt.setZone('local', {keepLocalTime: true}).toJSDate();
}

// Converts a time from an ant picker back into the user's selected timezone.
export function unapplyTimezone(d: Date, timezone: string): DateTime {
    return DateTime
        .fromJSDate(d)
        .setZone(timezone, {keepLocalTime: true});
}

export function formatDate(d: Date, timezone): string {
    return formatDateDt(DateTime.fromJSDate(d, {zone: timezone}));
}

export function formatDateDt(dt: DateTime): string {
    return dt.toLocaleString({
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
    });
}

export function formatTime(d: Date, timezone: string, hasTimezone: boolean): string {
    return formatTimeDt(DateTime.fromJSDate(d, {zone: timezone}), hasTimezone);
}

export function formatTimeDt(dt: DateTime, hasTimezone: boolean) {
    const timeConfig: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
    }

    // Only show the timezone if it's not the browser's default.
    if (hasTimezone) {
        timeConfig.timeZoneName = 'short';
    }

    return dt.toLocaleString(timeConfig);
}

export function formatDateAndTime(d: Date, timezone: string, hasTimezone: boolean): string {
    const dt = DateTime.fromJSDate(d, {zone: timezone});
    return formatDateAndTimeDt(dt, hasTimezone);
}

export function formatDateDay(d: Date, timezone: string): string {
    return DateTime.fromJSDate(d, {zone: timezone}).toISODate();
}

// Parses a date from the string format "2021-12-31"
// Returns [parsedDate, ok]
export function parseDateDay(dateStr: string, timezone: string): [Date, true] | [undefined, false] {
    const dt = DateTime.fromISO(dateStr, {zone: timezone});
    if (dt.isValid) {
        return [dt.toJSDate(), true];
    }
    return [undefined, false];
}

export function formatDateAndTimeDt(dt: DateTime, hasTimezone: boolean): string {
    return formatDateDt(dt) + ' at ' + formatTimeDt(dt, hasTimezone);
}

export function formatDurationShort(start: Date, end: Date, timezone: string, options?: any): string {
    const startDt = DateTime.fromJSDate(start, {zone: timezone});
    const endDt = DateTime.fromJSDate(end, {zone: timezone});
    return formatDurationShortDt(startDt, endDt, options);
}

export function formatDurationShortDt(start: DateTime, end: DateTime, options?: any): string {
    const interval = Interval.fromDateTimes(start, end);
    const dur = interval.toDuration();
    // Typescript note: luxon's type definitions are outdated, hence the cast.
    let durOptions: ToHumanDurationOptions = {
        unitDisplay: 'short',
        maximumFractionDigits: 1,
    } as any;

    if (options) {
        Object.assign(durOptions, options);
    }

    // By default, durations print as "5 days, 21 hours, 30 minutes, and 59 seconds".
    // This is too long, so we want to round to the most significant unit, like "5.9 days".
    if (dur.as('minutes') < 1) {
        return dur.shiftTo('seconds').toHuman(durOptions);
    }

    if (dur.as('hours') < 1) {
        return dur.shiftTo('minutes').toHuman(durOptions);
    }

    if (dur.as('days') < 1) {
        return dur.shiftTo('hours').toHuman(durOptions);
    }

    return dur.shiftTo('days').toHuman(durOptions);
}

// Luxon's interval.overlap method is actually an "open" check, meaning it excludes the endpoints.
// For example, the following intervals don't overlap using luxon's method: 
//   Sun 12:00am - Mon 12:00am, Mon 12:00am - Tue 12:00am 
// This is a problem because events that start or end at exactly 12:00am can be skipped.
// So, we're going to implement a half-open interval ovelap method.
// ORDER MATTERS: a.overlaps(b) !== b.overlaps(a). We need to be consistent in how we call it.
// Returns false if a.end abuts b.start.
//           [---b---]
//   [---a---]
// Returns true b.end abuts a.start
//   [---b---]
//           [---a---]

export function intervalsOverlap(a: Interval, b: Interval): boolean {
    return b.end >= a.start && b.start < a.end;
}

export interface ICancelablePromise<T> {
    promise: Promise<T>;
    cancel: () => void;
}

// If canceled, it silently ignores "then" and "catch" callbacks.
// This is useful for ignoring the results of async operations after a component is unmounted.
export function cancelablePromise<T>(p: Promise<T>): ICancelablePromise<T> {
    let isCanceled = false;
    const wrappedP = new Promise<T>((resolve, reject) => {
        p.then((result) => {
                if (!isCanceled) resolve(result);
            })
            .catch((e) => {
                if (!isCanceled) reject(e);
            });
    });

    return {
        promise: wrappedP,
        cancel: () => {
            isCanceled = true;
        }
    }
}

// Clamps "num" between "min" and "max".
// See: https://www.webtips.dev/webtips/javascript/how-to-clamp-numbers-in-javascript
export function clamp(num: number, min: number, max: number): number {
    return Math.min(Math.max(num, min), max);
}

// Maps a value in an input range to its equivalent in an output range.
// So, given a value of 15, in = 10 to 20, and out = 40 to 80, it outputs 60,
// because 15 is 50% of 10 to 20, and 50% of 40 to 80 is 60.
export function mapValue(val: number, inStart: number, inEnd: number, outStart: number, outEnd: number, clip = false): number {
    let denom1 = inEnd - inStart;
    // this is what percentage the input value is of the input range
    let inPercent = 0;
    if (denom1 !== 0) inPercent = (val - inStart) / denom1;

    const out = (inPercent * (outEnd - outStart)) + outStart;
    if (clip) {
        if (outStart < outEnd) {
            if (out < outStart) return outStart;
            if (out > outEnd) return outEnd;
            return out;
        } else {
            // outStart is greater than outEnd
            if (out < outEnd) return outEnd;
            if (out > outStart) return outStart;
            return out;
        }
    }
    return out;
}

export function isObject(val: any): boolean {
    return typeof val === 'object' && val !== null && !Array.isArray(val);
}

export function pluralize(count: number, singular: string, plural: string): string {
    if (count === 1) {
        return singular;
    }
    return plural;
}

export class Stale {
    private lastCallId = 1;

    // Returns a callId that can be used to check whether the call is stale.
    public startCall = (): number => {
        return ++this.lastCallId;
    }

    public isStale = (callId: number): boolean => {
        return this.lastCallId !== callId;
    }
}

// Useful for making sure a switch covers every case at compile time.
// https://stackoverflow.com/questions/39419170/how-do-i-check-that-a-switch-block-is-exhaustive-in-typescript
export function assertUnreachable(x: never): never {
    throw new Error(`Unexpected state error.`);
}